import React from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { FaLock, FaKey, FaFileAlt, FaCopy, FaDownload, FaCheckCircle, FaExclamationCircle, FaSpinner, FaShieldAlt, FaLockOpen, FaSearch } from "react-icons/fa";
import { analyzeContent } from "@/lib/api";
import CryptoJS from 'crypto-js';
import { toast } from "@/components/ui/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

const ENCRYPTION_MAGIC_SIGNATURE = "TRUTHNEXUS_ENCRYPTED_FILE_SIG_V1.0::";

// Helper function to convert WordArray to ArrayBuffer
function wordArrayToArrayBuffer(wordArray: CryptoJS.lib.WordArray): ArrayBuffer {
  const { words, sigBytes } = wordArray;
  const u8 = new Uint8Array(sigBytes);
  for (let i = 0; i < sigBytes; i++) {
    const byte = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
    u8[i] = byte;
  }
  return u8.buffer;
}

interface FeedbackCriterion {
  id: string;
  text: string;
  isMet: boolean;
  suggestionMet?: string;
  suggestionNotMet?: string;
}

interface HistoryItem {
  type: string;
  title: string;
  description: string;
  timestamp: Date;
  status: 'success' | 'error' | 'info' | 'warning';
  details?: Record<string, any>;
}

interface EncryptedOutput {
  type: 'text' | 'file';
  data: string | ArrayBuffer | null;
  fileName?: string;
}

const CyberThreat = () => {
  const [password, setPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState<string | null>(null);
  const [passwordCriteria, setPasswordCriteria] = useState<FeedbackCriterion[] | null>(null);
  const [isCheckingPassword, setIsCheckingPassword] = useState(false);

  // State for Encryption
  const [encryptInputType, setEncryptInputType] = useState('text'); // 'text' or 'file'
  const [encryptText, setEncryptText] = useState("");
  const [encryptFile, setEncryptFile] = useState<File | null>(null);
  const [encryptPassword, setEncryptPassword] = useState("");
  const [encryptedOutput, setEncryptedOutput] = useState<EncryptedOutput | null>(null);
  const [isEncrypting, setIsEncrypting] = useState(false);
  const [encryptError, setEncryptError] = useState("");

  // State for Decryption
  const [decryptInputType, setDecryptInputType] = useState('text'); // 'text' or 'file'
  const [decryptText, setDecryptText] = useState("");
  const [decryptFile, setDecryptFile] = useState<File | null>(null);
  const [decryptPassword, setDecryptPassword] = useState("");
  const [decryptedOutput, setDecryptedOutput] = useState<EncryptedOutput | null>(null);
  const [isDecrypting, setIsDecrypting] = useState(false);
  const [decryptError, setDecryptError] = useState("");

  const [activeTab, setActiveTab] = useState('password-check');

  const checkPasswordStrength = async () => {
    if (!password) return;
    setIsCheckingPassword(true);
    setPasswordStrength(null);
    setPasswordCriteria(null);

    try {
      // Basic strength check (can be replaced with a library later)
      let strength = "Weak";
      // Simplified checks - more robust check needed for production
      const hasMinLength = password.length >= 8;
      const hasUppercase = /[A-Z]/.test(password);
      const hasLowercase = /[a-z]/.test(password);
      const hasNumber = /[0-9]/.test(password);
      const hasSymbol = /[^A-Za-z0-9]/.test(password);

      if (password.length >= 12 && hasUppercase && hasLowercase && hasNumber && hasSymbol) {
        strength = "Strong";
      } else if (password.length >= 8 && (hasUppercase || hasLowercase) && (hasNumber || hasSymbol)) {
        strength = "Medium";
      }

      setPasswordStrength(strength);

      const criteria: FeedbackCriterion[] = [];
      const minLengthMet = password.length >= 8;
      const uppercaseMet = /[A-Z]/.test(password);
      const lowercaseMet = /[a-z]/.test(password);
      const numberMet = /[0-9]/.test(password);
      const symbolMet = /[^A-Za-z0-9]/.test(password);

      criteria.push({
        id: 'minLength',
        text: 'At least 8 characters',
        isMet: minLengthMet,
        suggestionMet: 'Has 8 or more characters.',
        suggestionNotMet: 'Needs to be at least 8 characters long.'
      });
      criteria.push({
        id: 'uppercase',
        text: 'Uppercase letter (A-Z)',
        isMet: uppercaseMet,
        suggestionMet: 'Includes at least one uppercase letter.',
        suggestionNotMet: 'Add at least one uppercase letter (A-Z).'
      });
      criteria.push({
        id: 'lowercase',
        text: 'Lowercase letter (a-z)',
        isMet: lowercaseMet,
        suggestionMet: 'Includes at least one lowercase letter.',
        suggestionNotMet: 'Add at least one lowercase letter (a-z).'
      });
      criteria.push({
        id: 'number',
        text: 'Number (0-9)',
        isMet: numberMet,
        suggestionMet: 'Includes at least one number.',
        suggestionNotMet: 'Add at least one number (0-9).'
      });
      criteria.push({
        id: 'symbol',
        text: 'Symbol (e.g., !@#$)',
        isMet: symbolMet,
        suggestionMet: 'Includes at least one symbol.',
        suggestionNotMet: 'Add at least one symbol (e.g., !@#$).'
      });

      if (strength === "Strong") {
        criteria.push({
          id: 'overallStrong',
          text: 'Overall Assessment',
          isMet: true,
          suggestionMet: 'Excellent! This is a strong password.',
          suggestionNotMet: '' // Not applicable
        });
      }

      setPasswordCriteria(criteria);

    } catch (error) {
      console.error("Password strength check failed:", error);
      setPasswordStrength("Error");
    } finally {
      setIsCheckingPassword(false);
      // TODO: Call saveHistoryItem here after implementing Firebase integration
      // For now, log to console as a placeholder
      saveHistoryItem({
        type: 'password-check',
        title: 'Password Strength Check',
        description: `Checked strength for a password of length ${password.length}`,
        timestamp: new Date(),
        status: passwordStrength === 'Error' ? 'error' : 'success', // Use the final status
        details: {
          strength: passwordStrength || 'N/A',
          inputLength: password.length
        }
      });
    }
  };

  const handleEncrypt = async () => {
    setEncryptError("");
    setEncryptedOutput(null);

    if (!encryptPassword) {
      setEncryptError("Please enter an encryption password.");
      return;
    }

    if (encryptInputType === 'text' && !encryptText.trim()) {
      setEncryptError("Please enter text to encrypt.");
      return;
    }

    if (encryptInputType === 'file' && !encryptFile) {
      setEncryptError("Please upload a file to encrypt.");
      return;
    }

    setIsEncrypting(true);
    try {
      // Use the same simple key derivation as decryption
      const passwordHash = CryptoJS.SHA256(encryptPassword).toString(); // Simple key derivation for demo
      let encryptedData: string | ArrayBuffer | null = null;

      if (encryptInputType === 'text' && encryptText) {
        encryptedData = CryptoJS.AES.encrypt(encryptText, passwordHash).toString();
        setEncryptedOutput({ type: 'text', data: encryptedData });
      } else if (encryptInputType === 'file' && encryptFile) {
        // Read file as ArrayBuffer
        const fileArrayBuffer = await encryptFile.arrayBuffer();
        const fileWordArray = CryptoJS.lib.WordArray.create(fileArrayBuffer as any);

        // Create WordArray for the magic signature
        const signatureWordArray = CryptoJS.enc.Utf8.parse(ENCRYPTION_MAGIC_SIGNATURE);

        // Concatenate signature and file content
        const combinedWordArray = signatureWordArray.concat(fileWordArray);

        // Encrypt combined WordArray
        const encryptedResult = CryptoJS.AES.encrypt(combinedWordArray, passwordHash);

        // Convert encrypted WordArray to Base64 string for download
        encryptedData = encryptedResult.toString();
        setEncryptedOutput({ type: 'file', data: encryptedData, fileName: `${encryptFile.name}.encrypted` });
      }

      toast({ title: "Success", description: "Content encrypted successfully." });

    } catch (error) {
      console.error("Encryption failed:", error);
      setEncryptError("Encryption failed. Please check the password and input.");
      toast({ title: "Error", description: "Encryption failed.", variant: "destructive" });
    } finally {
      setIsEncrypting(false);
    }
  };

   // Helper to convert CryptoJS WordArray to ArrayBuffer
   const wordArrayToArrayBuffer = (wordArray: CryptoJS.lib.WordArray): ArrayBuffer => {
    const len = wordArray.sigBytes;
    const words = wordArray.words;
    const ab = new ArrayBuffer(len);
    const view = new Uint8Array(ab);
    for (let i = 0; i < len; i++) {
        view[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
    }
    return ab;
  };

  const handleDownload = (data: BlobPart, filename: string) => {
    const blob = new Blob([data], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => toast({ title: "Copied!", description: "Encrypted text copied to clipboard." }))
      .catch(err => toast({ title: "Copy Failed", description: "Could not copy text.", variant: "destructive" }));
  };

  const handleDecrypt = async () => {
    setDecryptError("");
    setDecryptedOutput(null);

    if (!decryptPassword) {
      setDecryptError("Please enter the decryption password.");
      return;
    }

    if (decryptInputType === 'text' && !decryptText.trim()) {
      setDecryptError("Please enter text to decrypt.");
      return;
    }

    if (decryptInputType === 'file' && !decryptFile) {
      setDecryptError("Please upload an encrypted file.");
      return;
    }

    setIsDecrypting(true);
    try {
      const passwordHash = CryptoJS.SHA256(decryptPassword).toString();
      let decryptedData: string | ArrayBuffer | null = null;

      if (decryptInputType === 'text' && decryptText) {
        const bytes = CryptoJS.AES.decrypt(decryptText, passwordHash);
        // Attempt to convert to UTF-8. If the password is wrong, this might fail or result in garbage.
        decryptedData = bytes.toString(CryptoJS.enc.Utf8);

        // Check if the decrypted data seems valid UTF-8 (a simple check)
        // A more robust check might be needed depending on expected content
        if (!decryptedData || decryptedData.length === 0) { // Basic check for empty result
            throw new Error("Incorrect password or invalid encrypted text.");
        }

        setDecryptedOutput({ type: 'text', data: decryptedData });

      } else if (decryptInputType === 'file' && decryptFile) {
         // Read the encrypted file content (assuming it's Base64 string as saved during encryption)
        const fileContent = await decryptFile.text(); // Read as text to get the Base64 string

        const bytes = CryptoJS.AES.decrypt(fileContent, passwordHash);

        const decryptedArrayBuffer = wordArrayToArrayBuffer(bytes);

      const magicSignatureBytes = new TextEncoder().encode(ENCRYPTION_MAGIC_SIGNATURE);
      const signatureLength = magicSignatureBytes.length;

      if (decryptedArrayBuffer.byteLength < signatureLength) {
        throw new Error("Decrypted file is too short to contain signature. Incorrect password or corrupted file.");
      }

      const decryptedSignatureBytes = new Uint8Array(decryptedArrayBuffer, 0, signatureLength);

      let signatureMatch = true;
      for (let i = 0; i < signatureLength; i++) {
        if (decryptedSignatureBytes[i] !== magicSignatureBytes[i]) {
          signatureMatch = false;
          break;
        }
      }

      if (!signatureMatch) {
        throw new Error("Incorrect password or file corrupted. Signature mismatch.");
      }

      // If signature matches, extract the original file content
      const originalFileArrayBuffer = decryptedArrayBuffer.slice(signatureLength);
      decryptedData = originalFileArrayBuffer;

        // Try to derive original filename (if '.encrypted' suffix was added)
        const originalFileName = decryptFile.name.endsWith('.encrypted') ? decryptFile.name.slice(0, -'.encrypted'.length) : decryptFile.name + '.decrypted';


        setDecryptedOutput({ type: 'file', data: decryptedData, fileName: originalFileName });
      }

      toast({ title: "Success", description: "Content decrypted successfully." });

    } catch (error) {
      console.error("Decryption failed:", error);
      setDecryptError(`Decryption failed: ${(error as Error).message || String(error)}`); // Display specific error message
      toast({ title: "Error", description: "Decryption failed.", variant: "destructive" });
    } finally {
      setIsDecrypting(false);
    }
  };

  const getPasswordStrengthColor = (strength: string): string => {
    switch (strength) {
      case 'Strong': return 'bg-green-500';
      case 'Medium': return 'bg-yellow-500';
      case 'Weak': return 'bg-red-500';
      default: return "bg-gray-500";
    };
  };

  const getPasswordStrengthValue = (strength: string): number => {
    switch (strength) {
      case 'Strong': return 100;
      case 'Medium': return 66;
      case 'Weak': return 33;
      default: return 0;
    }
  };

  const isDecryptButtonDisabled = isDecrypting || 
                                (decryptInputType === 'text' && !decryptText.trim()) || 
                                (decryptInputType === 'file' && !decryptFile);

  // Placeholder function to save history item (replace with Firebase logic)
  const saveHistoryItem = (item: HistoryItem) => {
    console.log("Saving history item:", item);
    // TODO: Implement actual saving to Firebase here
    // You will need to add logic to connect to Firebase,
    // authenticate the user, and write the item to a Firestore collection
    // e.g., db.collection('users').doc(currentUser.uid).collection('history').add(item);
  }; // Ensured semicolon

  return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-900 to-black py-6 px-2 sm:px-4">
        <Card className="w-full max-w-6xl mx-auto bg-black/20 backdrop-blur-sm border-gray-800 shadow-xl">
          <CardHeader className="space-y-2 sm:space-y-4">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
              <FaShieldAlt className="text-3xl sm:text-4xl text-truth" />
              <CardTitle className="text-2xl sm:text-3xl font-bold text-center text-truth">
                {activeTab === 'encryption' || activeTab === 'decryption' ? 'Secure Communication' : 'Cyber Threat Analysis'}
              </CardTitle>
            </div>
            <p className="text-center text-sm sm:text-base text-muted-foreground">Tools to analyze password strength, encrypt/decrypt data, and more.</p>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="password-check" onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 bg-gray-700/50 border-gray-600 p-1">
                <TabsTrigger value="password-check" className="text-gray-300 data-[state=active]:bg-truth data-[state=active]:text-black data-[state=active]:font-semibold transition-colors duration-200">Pass. Check</TabsTrigger>
                <TabsTrigger value="encryption" className="text-gray-300 data-[state=active]:bg-truth data-[state=active]:text-black data-[state=active]:font-semibold transition-colors duration-200">Encrypt</TabsTrigger>
                <TabsTrigger value="decryption" className="text-gray-300 data-[state=active]:bg-truth data-[state=active]:text-black data-[state=active]:font-semibold transition-colors duration-200">Decrypt</TabsTrigger>
              </TabsList>
              <TabsContent value="password-check" className="mt-4 space-y-4 sm:space-y-6 p-2 sm:p-4 bg-black/30 rounded-lg border border-gray-700">
                <div className="space-y-2">
                  <h4 className="text-lg font-semibold text-truth">Pass. Check</h4>
                  <p className="text-sm text-muted-foreground">Enter a password to check its strength and get suggestions.</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <Input
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="flex-1 bg-black/50 border-gray-600 placeholder-gray-500 text-white focus:border-truth"
                  />
                  <Button onClick={checkPasswordStrength} disabled={isCheckingPassword || !password.trim()} className="bg-truth hover:bg-truth/90 text-black flex-shrink-0">
                    {isCheckingPassword ? ( <FaSpinner className="animate-spin mr-2" />) : (<FaSearch className="mr-2" />) }Check Strength
                  </Button>
                </div>
                
                {passwordStrength && ( 
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-300">Strength:</p>
                      <Badge className={`${getPasswordStrengthColor(passwordStrength)} text-sm font-semibold`}>{passwordStrength}</Badge>
                    </div>

                    {/* Progress bar for visual strength indication */}
                    <Progress value={getPasswordStrengthValue(passwordStrength)} className={`w-full bg-gray-700 ${getPasswordStrengthColor(passwordStrength)}`} />

                    {passwordCriteria && passwordCriteria.length > 0 && (
                      <div className="space-y-2 pt-2 border-t border-gray-700">
                        <p className="text-sm font-medium text-gray-300">Criteria:</p>
                        <ul className="space-y-1 text-sm text-gray-400">
                          {passwordCriteria.map((criterion) => (
                            <li key={criterion.id} className="flex items-center gap-2">
                              {criterion.isMet ? (
                                <FaCheckCircle className="text-green-500 flex-shrink-0" />
                              ) : (
                                <FaExclamationCircle className="text-red-500 flex-shrink-0" />
                              )}
                              {criterion.text}: {criterion.isMet ? 
                                (<span className="text-green-400">{criterion.suggestionMet}</span>) : 
                                (<span className="text-red-400">{criterion.suggestionNotMet}</span>)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

              </TabsContent>
              <TabsContent value="encryption" className="mt-4 space-y-4 sm:space-y-6 p-2 sm:p-4 bg-black/30 rounded-lg border border-gray-700">
                <div className="space-y-2">
                  <h4 className="text-lg font-semibold text-truth">Encrypt Data</h4>
                  <p className="text-sm text-muted-foreground">Encrypt your text or files with a password.</p>
                </div>
                <div className="bg-blue-900/20 border border-blue-700 text-blue-300 text-sm p-3 rounded-md flex items-center gap-3">
                  <FaShieldAlt className="text-xl flex-shrink-0" />
                  <p>
                    <span className="text-truth font-semibold">Your data stays private:</span> Encryption is performed directly on your device using advanced cryptography technology. Your text or files are <span className="text-red-400 font-semibold">never sent to any server</span> during this process and cannot be viewed by any other person.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <div className="flex w-full sm:w-[150px] rounded-md border border-gray-600 bg-black/50">
                    <Button
                      variant="ghost"
                      className={`flex-1 rounded-none ${encryptInputType === 'text' ? 'bg-truth text-black hover:bg-truth/90' : 'text-gray-300 hover:bg-gray-700/50'}`}
                      onClick={() => {setEncryptInputType('text'); setEncryptFile(null); setEncryptText("");}}
                    >
                      Text
                    </Button>
                    <Button
                      variant="ghost"
                      className={`flex-1 rounded-none border-l border-gray-600 ${encryptInputType === 'file' ? 'bg-truth text-black hover:bg-truth/90' : 'text-gray-300 hover:bg-gray-700/50'}`}
                      onClick={() => {setEncryptInputType('file'); setEncryptText(""); setEncryptFile(null);}}
                    >
                      File
                    </Button>
                  </div>
                   <Input
                    type="password"
                    placeholder="Enter encryption password"
                    value={encryptPassword}
                    onChange={(e) => setEncryptPassword(e.target.value)}
                    className="flex-1 bg-black/50 border-gray-600 placeholder-gray-500 text-white focus:border-truth"
                  />
                </div>

                {encryptInputType === 'text' ? (
                   <Textarea
                    placeholder="Enter text to encrypt..."
                    value={encryptText}
                    onChange={(e) => setEncryptText(e.target.value)}
                    className="min-h-[150px] bg-black/50 border-gray-600 placeholder-gray-500 text-white focus:border-truth"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center border border-dashed border-gray-600 rounded-lg p-6 text-center space-y-3 bg-black/30">
                     <input
                      type="file"
                      onChange={(e) => setEncryptFile(e.target.files ? e.target.files[0] : null)}
                      className="hidden"
                      id="encrypt-file-upload"
                    />
                    <label htmlFor="encrypt-file-upload" className="cursor-pointer text-truth hover:text-truth/80">
                      <FaFileAlt className="text-4xl mb-2 mx-auto" />
                      <p className="text-sm text-gray-400">Drag and drop a file here or click to select</p>
                      {encryptFile && <p className="text-xs text-gray-300 mt-1">Selected file: {encryptFile.name}</p>}
                    </label>
                  </div>
                )}

                 <Button onClick={handleEncrypt} disabled={isEncrypting || (!encryptText.trim() && !encryptFile) || !encryptPassword.trim()} className="w-full bg-truth hover:bg-truth/90 text-black">
                  {isEncrypting ? ( <FaSpinner className="animate-spin mr-2" />) : (<FaLock className="mr-2" />) }Encrypt
                </Button>

                {encryptError && (
                   <div className="text-red-400 text-sm mt-2">{encryptError}</div>
                )}

                {encryptedOutput && ( 
                  <div className="mt-4 space-y-3 p-3 bg-black/40 rounded-lg border border-gray-700">
                    <h5 className="text-md font-semibold text-truth">Encrypted Output:</h5>
                    {encryptedOutput.type === 'text' && typeof encryptedOutput.data === 'string' ? (
                      <Textarea
                        readOnly
                        value={encryptedOutput.data}
                        className="min-h-[100px] bg-black/50 border-gray-600 text-white cursor-text"
                      />
                    ) : encryptedOutput.type === 'file' && typeof encryptedOutput.data === 'string' ? (
                      <div className="flex items-center justify-between text-sm text-gray-300">
                         <span>{encryptedOutput.fileName || 'encrypted_file'}</span>
                         <Button variant="outline" size="sm" onClick={() => handleDownload(encryptedOutput.data as string, encryptedOutput.fileName || 'encrypted_file')} className="text-truth hover:text-truth/80 border-truth/50 hover:bg-truth/10">
                           <FaDownload className="mr-2" /> Download
                        </Button>
                      </div>
                    ) : null}

                    {/* Add Copy Button for Text Output */}
                    {encryptedOutput.type === 'text' && typeof encryptedOutput.data === 'string' && encryptedOutput.data.length > 0 && (
                      <Button variant="outline" size="sm" onClick={() => handleCopy(encryptedOutput.data as string)} className="text-gray-400 hover:text-truth border-gray-600 hover:bg-gray-700/50">
                        <FaCopy className="mr-2" /> Copy Output
                      </Button>
                    )}
                  </div>
                )}

              </TabsContent>
              <TabsContent value="decryption" className="mt-4 space-y-4 sm:space-y-6 p-2 sm:p-4 bg-black/30 rounded-lg border border-gray-700">
                <div className="space-y-2">
                  <h4 className="text-lg font-semibold text-truth">Decrypt Data</h4>
                  <p className="text-sm text-muted-foreground">Decrypt your encrypted text or files with the correct password.</p>
                </div>
                <div className="bg-blue-900/20 border border-blue-700 text-blue-300 text-sm p-3 rounded-md flex items-center gap-3">
                  <FaShieldAlt className="text-xl flex-shrink-0" />
                  <p>
                    <span className="text-truth font-semibold">Your data stays private:</span> Decryption is performed directly on your device using advanced cryptography technology. Your encrypted text or files are <span className="text-red-400 font-semibold">never sent to any server</span> during this process and cannot be viewed by any other person.
                  </p>
                </div>
                 <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                   <div className="flex w-full sm:w-[150px] rounded-md border border-gray-600 bg-black/50">
                     <Button
                       variant="ghost"
                       className={`flex-1 rounded-none ${decryptInputType === 'text' ? 'bg-truth text-black hover:bg-truth/90' : 'text-gray-300 hover:bg-gray-700/50'}`}
                       onClick={() => {setDecryptInputType('text'); setDecryptFile(null); setDecryptText("");}}
                     >
                       Text
                     </Button>
                     <Button
                       variant="ghost"
                       className={`flex-1 rounded-none border-l border-gray-600 ${decryptInputType === 'file' ? 'bg-truth text-black hover:bg-truth/90' : 'text-gray-300 hover:bg-gray-700/50'}`}
                       onClick={() => {setDecryptInputType('file'); setDecryptText(""); setDecryptFile(null);}}
                     >
                       File
                     </Button>
                   </div>
                    <Input
                     type="password"
                     placeholder="Enter decryption password"
                     value={decryptPassword}
                     onChange={(e) => setDecryptPassword(e.target.value)}
                     className="flex-1 bg-black/50 border-gray-600 placeholder-gray-500 text-white focus:border-truth"
                   />
                 </div>

                {decryptInputType === 'text' ? (
                   <Textarea
                    placeholder="Enter encrypted text..."
                    value={decryptText}
                    onChange={(e) => setDecryptText(e.target.value)}
                    className="min-h-[150px] bg-black/50 border-gray-600 placeholder-gray-500 text-white focus:border-truth"
                  />
                ) : (
                   <div className="flex flex-col items-center justify-center border border-dashed border-gray-600 rounded-lg p-6 text-center space-y-3 bg-black/30">
                     <input
                      type="file"
                      onChange={(e) => setDecryptFile(e.target.files ? e.target.files[0] : null)}
                      className="hidden"
                      id="decrypt-file-upload"
                    />
                    <label htmlFor="decrypt-file-upload" className="cursor-pointer text-truth hover:text-truth/80">
                      <FaFileAlt className="text-4xl mb-2 mx-auto" />
                      <p className="text-sm text-gray-400">Drag and drop an encrypted file here or click to select</p>
                      {decryptFile && <p className="text-xs text-gray-300 mt-1">Selected file: {decryptFile.name}</p>}
                    </label>
                  </div>
                )}

                 <Button onClick={handleDecrypt} disabled={isDecrypting || (!decryptText.trim() && !decryptFile) || !decryptPassword.trim()} className="w-full bg-truth hover:bg-truth/90 text-black">
                  {isDecrypting ? ( <FaSpinner className="animate-spin mr-2" />) : (<FaLockOpen className="mr-2" />) }Decrypt
                </Button>

                {decryptError && (
                   <div className="text-red-400 text-sm mt-2">{decryptError}</div>
                )}

                 {decryptedOutput && ( 
                  <div className="mt-4 space-y-3 p-3 bg-black/40 rounded-lg border border-gray-700">
                    <h5 className="text-md font-semibold text-truth">Decrypted Output:</h5>
                    {decryptedOutput.type === 'text' && typeof decryptedOutput.data === 'string' ? (
                      <Textarea
                        readOnly
                        value={decryptedOutput.data}
                        className="min-h-[100px] bg-black/50 border-gray-600 text-white cursor-text"
                      />
                    ) : decryptedOutput.type === 'file' && decryptedOutput.data instanceof ArrayBuffer ? (
                      <div className="flex items-center justify-between text-sm text-gray-300">
                         <span>{decryptedOutput.fileName || 'decrypted_file'}</span>
                         <Button variant="outline" size="sm" onClick={() => handleDownload(decryptedOutput.data as ArrayBuffer, decryptedOutput.fileName || 'decrypted_file')} className="text-truth hover:text-truth/80 border-truth/50 hover:bg-truth/10">
                           <FaDownload className="mr-2" /> Download
                        </Button>
                      </div>
                    ) : null}

                    {/* Add Copy Button for Text Output */}
                    {decryptedOutput.type === 'text' && typeof decryptedOutput.data === 'string' && decryptedOutput.data.length > 0 && (
                      <Button variant="outline" size="sm" onClick={() => handleCopy(decryptedOutput.data as string)} className="text-gray-400 hover:text-truth border-gray-600 hover:bg-gray-700/50">
                        <FaCopy className="mr-2" /> Copy Output
                      </Button>
                    )}
                  </div>
                )}

              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default CyberThreat;